import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';

import { ModalContent } from '@ap/modal';
import { ApContentService, ApKeyOverridePipe, ApContentPipe } from '@ap/content';
import { ApRadioGroupComponent, ApRadioButtonComponent } from '@ap/radio';
import { ApButtonComponent } from '@ap/button';
import { ApFormControlPipe } from '@ap/cdk';

import { ApEmploymentIncomeComponent } from '../employment-income/employment-income.component';
import { ApEntrepreneurIncomeComponent } from '../entrepreneur-income/entrepreneur-income.component';
import { EntrepreneurIncomeForm, IncomeModalOptions, IncomeTypeForm, MonthlyIncomeForm } from '../income.model';

import content from './calculate-income.content.json';

@Component({
    selector: 'ap-calculate-income',
    templateUrl: './calculate-income.component.html',
    imports: [
        ApRadioGroupComponent,
        ApRadioButtonComponent,
        FormsModule,
        ReactiveFormsModule,
        ApEntrepreneurIncomeComponent,
        ApEmploymentIncomeComponent,
        ApButtonComponent,
        ApFormControlPipe,
        ApContentPipe,
        ApKeyOverridePipe
    ]
})
export class ApCalculateIncomeComponent extends ModalContent<IncomeModalOptions> implements OnInit, OnDestroy {
    contentService = inject(ApContentService);

    private incomeTypeSubscription: Subscription;
    public showEntrepreneurIncome: boolean;
    public incomeTypeForm: FormGroup<IncomeTypeForm>;
    public monthlyIncomeForm: FormGroup<MonthlyIncomeForm>;
    public entrepreneurIncomeForm: FormGroup<EntrepreneurIncomeForm>;
    public numberOfDecimals = 2;

    public contentKeyMap = {};

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.showEntrepreneurIncome = this.modalOptions.options.showEntrepreneurIncome;
        this.entrepreneurIncomeForm = this.modalOptions.options.entrepreneurIncomeForm;
        this.numberOfDecimals = this.modalOptions.options.numberOfDecimals;
        this.monthlyIncomeForm = this.modalOptions.options.incomeForm;
        this.contentKeyMap = this.modalOptions.options.contentKeyMap;
        this.incomeTypeForm = this.modalOptions.options.incomeTypeForm;
        this.resetFormsOnTypeChange();
    }

    private resetFormsOnTypeChange() {
        this.incomeTypeSubscription = this.incomeTypeForm.controls.type.valueChanges.subscribe((incomeType) => {
            if (incomeType === 'entrepreneur') {
                const frequency = this.monthlyIncomeForm.controls.frequency.value;
                this.monthlyIncomeForm.reset();
                this.monthlyIncomeForm.controls.frequency.setValue(frequency);
            } else if (incomeType === 'employment') {
                const nrOfFinancialYears = this.entrepreneurIncomeForm.controls.nrOfFinancialYears.value;
                this.entrepreneurIncomeForm.reset();
                this.entrepreneurIncomeForm.controls.nrOfFinancialYears.setValue(nrOfFinancialYears);
            }
        });
    }

    public ngOnDestroy(): void {
        this.incomeTypeSubscription.unsubscribe();
    }

    public calculateClicked(): void {
        this.recursiveMarkAsDone(this.monthlyIncomeForm);
        if (this.monthlyIncomeForm.valid && this.incomeTypeForm.controls.type.value === 'employment') {
            this.accept({
                type: this.incomeTypeForm.controls.type.value,
                monthlyIncome: this.monthlyIncomeForm.value
            });
        }
        this.recursiveMarkAsDone(this.entrepreneurIncomeForm);
        if (this.entrepreneurIncomeForm.valid && this.incomeTypeForm.controls.type.value === 'entrepreneur') {
            this.accept({
                type: this.incomeTypeForm.controls.type.value,
                entrepreneurIncome: this.entrepreneurIncomeForm.value
            });
        }
    }

    private recursiveMarkAsDone(form: AbstractControl): void {
        if (form.disabled) {
            return;
        }
        form.markAsDirty({ onlySelf: true });
        form.markAsTouched({ onlySelf: true });
        form.updateValueAndValidity({ onlySelf: true });
        if (form instanceof FormGroup) {
            Object.keys(form.controls).forEach((key) => this.recursiveMarkAsDone(form.controls[key]));
        }
    }
}
